import React from 'react'
import PropTypes from 'prop-types'
import { Route, Switch } from 'react-router-dom'
import Router from 'shared/universal-router'
import { applyMiddleware, combineReducers, createStore } from 'redux'
import thunk from 'redux-thunk'
import { Provider } from 'react-redux'
import { createLogger } from 'redux-logger'
import { registryPath } from 'lib/urls'
import * as consts from 'registry/constants'
import { track, withContextualizedTracking } from 'lib/analytics'
import registryReducer from '../reducers'
import {
  setUser,
  setRegistry,
  setIsMobile,
  setActiveModal,
  setRegistryGuestViewOptimizationFeatureFlag,
} from '../actions'
import RegistryPageGuest from './registry-page-guest'
import {
  CrowdfundReservationModal,
  FundThisGiftModal,
  RecoverReservationModal,
  RegistryViewPurchaseInfoModal,
  RedirectModal,
  ReservationModal,
  ContactInfoModal,
  AddToCartModal,
  ReservePurchasedGiftModal,
  GiftMessageModal,
  GiftTrackerModal,
  UnreserveModal,
  ThankYouAddressModal,
} from '../modals'

const middlewares = [thunk]

if (process.env.NODE_ENV === 'development') {
  middlewares.push(createLogger())
}

const initReduxDevTools = () => {
  if (
    typeof window === 'object' &&
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
  ) {
    return window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__()
  }
}

const createStoreWithMiddleware = applyMiddleware(...middlewares)(createStore)

class RegistryGuestApp extends React.Component {
  constructor(props) {
    super(props)
    this.store = createStoreWithMiddleware(
      combineReducers(registryReducer),
      initReduxDevTools()
    )

    this.store.dispatch(setUser(props.context.user))
    this.store.dispatch(setRegistry(props.context.registry))
    this.store.dispatch(
      setRegistryGuestViewOptimizationFeatureFlag(
        props.registryGuestViewOptimizationFeatureFlag
      )
    )

    this.store.dispatch(setIsMobile(props.context.isMobile))
  }

  getChildContext() {
    const { context } = this.props
    return context
  }

  componentDidMount() {
    const { showInternationalUserModal } = this.props
    if (showInternationalUserModal) {
      setTimeout(() => this.showModal(consts.INTERNATIONAL_USER), 4000)
    }

    // Track RegistryViewed for non-owner, non-partner or non-authed user.
    const { context } = this.props
    const { registry } = context
    const { ownerId } = context.registry
    const { user } = context
    const registries = user ? user.registries : []
    // If current registry is in user.registries they are an owner or partner on it.
    const registryCount = registries.filter(
      (reg) => reg.id === registry.id
    ).length
    if (registryCount === 0) {
      const registryId = context.registry.id
      track.registryViewed({
        registryId,
        registryOwnerId: ownerId,
        isRegistryOwner: false,
        eventLocation: track.EventLocation.REGISTRY,
      })
    }
  }

  showModal = (modalName) => this.store.dispatch(setActiveModal(modalName))

  decoratedRegistryComponent = ({ location }) => (
    <>
      <AddToCartModal />
      <CrowdfundReservationModal />
      <FundThisGiftModal />
      <RegistryViewPurchaseInfoModal />
      <RedirectModal />
      <ReservePurchasedGiftModal />
      <ReservationModal />
      <RecoverReservationModal />
      <ContactInfoModal />
      <GiftMessageModal />
      <ThankYouAddressModal />
      <GiftTrackerModal />
      <UnreserveModal />
      <RegistryPageGuest location={location} />
    </>
  )

  render() {
    const {
      context: {
        registry: { urlSlug },
      },
      path,
    } = this.props

    return (
      <Provider store={this.store}>
        <Router location={{ pathname: path }}>
          <Switch>
            <Route
              path={registryPath(urlSlug)}
              render={({ location }) =>
                this.decoratedRegistryComponent({
                  location,
                })
              }
            />
          </Switch>
        </Router>
      </Provider>
    )
  }
}

RegistryGuestApp.propTypes = {
  context: PropTypes.shape({
    bookmarkletJS: PropTypes.string.isRequired,
    enableInaccuracyReportForm: PropTypes.bool.isRequired,
    helloBabyBoxUnboxingVideoUrl: PropTypes.string.isRequired,
    isFacebookApp: PropTypes.bool.isRequired,
    isMobile: PropTypes.bool.isRequired,
    registry: PropTypes.object.isRequired,
    showExtensionInstall: PropTypes.bool,
    user: PropTypes.object,
  }).isRequired,
  path: PropTypes.string.isRequired,
  showInternationalUserModal: PropTypes.bool,
  registryGuestViewOptimizationFeatureFlag: PropTypes.bool,
}

RegistryGuestApp.defaultProps = {
  showInternationalUserModal: false,
  registryGuestViewOptimizationFeatureFlag: false,
}

RegistryGuestApp.childContextTypes = {
  bookmarkletJS: PropTypes.string,
  showExtensionInstall: PropTypes.bool,
  helloBabyBoxUnboxingVideoUrl: PropTypes.string,
  isFacebookApp: PropTypes.bool,
  isMobile: PropTypes.bool,
  enableInaccuracyReportForm: PropTypes.bool,
  registry: PropTypes.object.isRequired,
  user: PropTypes.object,
}

export default withContextualizedTracking({
  eventLocation: track.EventLocation.REGISTRY,
})(RegistryGuestApp)
